import * as React from 'react';
import map from 'lodash/map';
import { getDocument, type PDFDocumentProxy } from 'pdfjs-dist';
import { cn } from '@ai-content-hub/content-kit';
import { useElementUITranslations } from '../../i18n/elementUI';
import { ensurePdfWorkerConfigured } from './pdfWorkerSetup';
import { isNotADocumentPayload, sniffDocumentPayload } from './documentPayload';

const PDF_PAGE_SCALE = 1.25;

export interface PdfJsViewerProps {
    data: ArrayBuffer;
    className?: string;
}

interface PdfPageCanvasProps {
    pdf: PDFDocumentProxy;
    pageNumber: number;
}

const PdfPageCanvas: React.FC<PdfPageCanvasProps> = ({ pdf, pageNumber }) => {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);

    React.useEffect(() => {
        let cancelled = false;
        const canvas = canvasRef.current;
        if (!canvas) {
            return undefined;
        }

        void pdf.getPage(pageNumber).then((page) => {
            if (cancelled || !canvasRef.current) {
                return;
            }
            const viewport = page.getViewport({ scale: PDF_PAGE_SCALE });
            const context = canvas.getContext('2d');
            if (!context) {
                return;
            }
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const renderTask = page.render({ canvasContext: context, viewport });
            void renderTask.promise.catch(() => {});
        });

        return () => {
            cancelled = true;
        };
    }, [pdf, pageNumber]);

    return (
        <canvas
            ref={canvasRef}
            className="office-pdf-viewer-page mx-auto block max-w-full bg-background shadow-sm"
        />
    );
};

/**
 * Which of the three PDF failures happened.
 *
 * EAA-1501: this used to be one flat sentence, "Could not open this PDF file.", for every
 * cause — and the rejection object was discarded by an argument-less `.catch()`, so
 * neither the reader nor the console could tell an encrypted file from a damaged one
 * from a body that had never been a PDF at all. Naming the cause is the whole fix; the
 * detail (URL, byte preview, pdf.js exception name) goes to the console, not the UI.
 */
type PdfLoadFailure = 'wrong-body' | 'password' | 'damaged';

/** pdf.js reports encryption by exception name, and the name survives minification. */
function classifyPdfError(err: unknown): PdfLoadFailure {
    const name = (err as { name?: string } | null | undefined)?.name;
    return name === 'PasswordException' ? 'password' : 'damaged';
}

export const PdfJsViewer: React.FC<PdfJsViewerProps> = ({ data, className }) => {
    const et = useElementUITranslations();
    const [pdf, setPdf] = React.useState<PDFDocumentProxy | null>(null);
    const [failure, setFailure] = React.useState<PdfLoadFailure | null>(null);

    React.useEffect(() => {
        ensurePdfWorkerConfigured();
        let cancelled = false;
        let doc: PDFDocumentProxy | null = null;

        setFailure(null);
        setPdf(null);

        // Cheap pre-check so an error page never gets reported as a broken PDF. pdf.js
        // would reject with InvalidPDFException here, which reads as "your file is bad".
        const payloadKind = sniffDocumentPayload(data);
        if (payloadKind !== 'pdf') {
            console.error(
                '[PdfJsViewer] the bytes handed to the PDF viewer are not a PDF; refusing to '
                    + 'blame the file.',
                { payloadKind, byteLength: data.byteLength },
            );
            // An HTML/JSON/empty body never left the *document* side: something answered
            // the request with a page instead of the file. Anything else — a zip, an OLE
            // container, unrecognised bytes — really is a bad file under a .pdf name.
            setFailure(isNotADocumentPayload(payloadKind) ? 'wrong-body' : 'damaged');
            return undefined;
        }

        void getDocument({ data: data.slice(0) })
            .promise.then((loaded) => {
                if (cancelled) {
                    void loaded.destroy();
                    return;
                }
                doc = loaded;
                setPdf(loaded);
            })
            .catch((err: unknown) => {
                console.error('[PdfJsViewer] pdf.js could not open the document.', {
                    byteLength: data.byteLength,
                    name: (err as { name?: string } | null)?.name,
                    message: (err as { message?: string } | null)?.message,
                });
                if (!cancelled) {
                    setFailure(classifyPdfError(err));
                }
            });

        return () => {
            cancelled = true;
            if (doc) {
                void doc.destroy();
            }
        };
    }, [data]);

    if (failure) {
        const message =
            failure === 'wrong-body'
                ? et.documentServerError
                : failure === 'password'
                  ? et.documentPdfPasswordProtected
                  : et.documentPdfDamaged;
        return (
            <div
                className={cn(
                    'office-pdf-viewer office-pdf-viewer--error text-sm text-muted-foreground p-4',
                    className,
                )}
                data-failure={failure}
            >
                {message}
            </div>
        );
    }

    if (!pdf) {
        return (
            <div className={cn('office-pdf-viewer office-pdf-viewer--loading p-4 text-sm text-muted-foreground', className)}>
                Rendering PDF…
            </div>
        );
    }

    const count = pdf.numPages;
    const pageNumbers: number[] = [];
    for (let i = 1; i <= count; i += 1) {
        pageNumbers.push(i);
    }

    return (
        <div
            className={cn(
                'office-pdf-viewer flex max-h-full min-h-0 flex-col gap-4 overflow-y-auto p-4',
                className,
            )}
        >
            {map(pageNumbers, (pageNumber) => (
                <PdfPageCanvas key={pageNumber} pdf={pdf} pageNumber={pageNumber} />
            ))}
        </div>
    );
};
